import { FieldConfig, Option, SectionConfig } from "@platform/design-system";
import { useMemo } from "react";
import { FieldValues } from "react-hook-form";

type Field<TCustomType extends string> =
  | FieldConfig<TCustomType>
  | SectionConfig<TCustomType>;

const resolveBool = <TCustomType extends string, TValues extends FieldValues>(
  formState: TValues,
  field: Field<TCustomType>,
  input:
    | string
    | ((formState: TValues, field: Field<TCustomType>) => boolean)
    | undefined,
): boolean | undefined => {
  if (input === undefined) return undefined;
  if (typeof input === "function") return input(formState, field);
  if (typeof input === "string") {
    const negate = input.startsWith("!");
    const key = negate ? input.slice(1) : input;
    if (!Object.prototype.hasOwnProperty.call(formState, key)) {
      return negate ? true : false;
    }
    const value: unknown = formState[key];
    return negate ? !value : !!value;
  }
  return !!input;
};

interface UseFieldDependenciesReturn {
  isVisible: boolean;
  isDisabled: boolean;
  overrideValue: unknown;
  overrideOptions: Option[] | undefined;
}

function useFieldDependencies<
  TCustomType extends string,
  TValues extends FieldValues,
>(field: Field<TCustomType>, formState: TValues): UseFieldDependenciesReturn {
  const { dependencies } = field;

  const result = useMemo(() => {
    if (!dependencies) {
      return {
        isVisible: true,
        isDisabled: false,
        overrideValue: undefined,
        overrideOptions: undefined,
      };
    }

    const resolveValue = (
      input: string | ((formState: TValues) => unknown) | undefined,
    ): unknown => {
      if (input === undefined) return undefined;
      if (typeof input === "function") return input(formState);
      if (typeof input === "string") {
        return input in formState ? formState[input] : undefined;
      }
      return input;
    };

    const resolveOptions = (
      input: Option[] | ((formState: TValues) => Option[]) | undefined,
    ): Option[] | undefined => {
      if (input === undefined) return undefined;
      if (typeof input === "function") return input(formState);
      return input;
    };

    return {
      isVisible: resolveBool(formState, field, dependencies?.visible) ?? true,
      isDisabled:
        resolveBool(formState, field, dependencies?.disabled) ?? false,
      overrideValue: resolveValue(dependencies?.value),
      overrideOptions: resolveOptions(dependencies?.values),
    };
  }, [dependencies, formState, field]);

  return result;
}

export { useFieldDependencies };
